import { useMemo } from 'react'
import { computeRoundResults, getDormieSegments, computePressResults, computeSegmentMatchStatus, getPressLabel } from '../utils/scoringEngine'
import { getGameCourse, getGameTee, getMixedTees } from '../constants/courses'
import { buildGameHandicapBreakdown } from '../utils/buildGameHandicapBreakdown'
import { deserializeHandicapBreakdown } from '../utils/resultSnapshot'

/**
 * Consolidated hook for all game scoring computations.
 * Single source of truth — components consume the returned data without recomputing.
 */
export function useGameScoring(gameData, matchPlayers) {
  const teams = gameData?.teams || []
  const scores = gameData?.scores || []
  const gameType = gameData?.game_type
  const numHoles = gameData?.num_holes || 18
  const gameConfig = gameData?.game_config || {}

  const hasSnapshot = gameData?.status === 'completed' && !!gameData?.final_results?.roundResults

  const teamStructure = useMemo(() => {
    if (hasSnapshot) return gameData.final_results.teamStructure
    return teams
      .sort((a, b) => a.team_order - b.team_order)
      .map((t) => ({
        name: t.team_name,
        playerIds: (t.team_players || []).map((tp) => tp.player_id),
      }))
  }, [teams, hasSnapshot, gameData?.final_results?.teamStructure])

  const course = getGameCourse(gameData)
  const tee = getGameTee(gameData)
  const mixedTees = getMixedTees(gameData)

  const handicapBreakdown = useMemo(() => {
    if (hasSnapshot) return deserializeHandicapBreakdown(gameData.final_results.handicapBreakdown)
    return buildGameHandicapBreakdown({
      gameType,
      teamStructure,
      matchPlayers,
      gamePlayers: gameData?.game_players,
      course,
      tee,
      mixedTees,
      gameConfig,
    })
  }, [course, tee, mixedTees, matchPlayers, gameData?.game_players, gameType, teamStructure, hasSnapshot, gameData?.final_results?.handicapBreakdown, gameConfig])

  const results = useMemo(() => {
    if (hasSnapshot) return gameData.final_results.roundResults
    if (teamStructure.length < 2 || !gameType || gameType === 'stroke') return null
    const cfg = { ...gameConfig }
    if (course) cfg.holePars = course.par
    return computeRoundResults(gameType, teamStructure, scores, handicapBreakdown, numHoles, cfg)
  }, [gameType, teamStructure, scores, handicapBreakdown, numHoles, gameConfig, course, hasSnapshot, gameData?.final_results?.roundResults])

  const dormieSegments = useMemo(() => {
    if (hasSnapshot) return []
    return getDormieSegments(results)
  }, [results, hasSnapshot])

  // Match play running scores per hole (front/back/overall + clinch detection)
  // High-Low uses point differential display instead (routed through strokePlayTeamScores)
  const isHighLow = gameType === 'high_low'
  const matchPlayHoleStatus = useMemo(() => {
    if (isHighLow) return null
    if (!results?.holeResults || !results?.nassau) return null
    const hr = results.holeResults
    const computeRunning = (start, end) => {
      const s = {}
      let diff = 0
      for (let h = start; h <= end; h++) {
        const r = hr.find(x => x.hole === h)
        if (r && !r.pending) {
          if (r.teamPoints) {
            diff += (r.teamPoints[0] - r.teamPoints[1])
          } else {
            if (r.winner === 0) diff++
            else if (r.winner === 1) diff--
          }
          s[h] = diff
        }
      }
      return s
    }
    const toClinch = (result) => {
      if (!result || result.clinchHole == null) return null
      return { clinchHole: result.clinchHole, text: result.text.replace(/ /g, ''), teamAWon: result.winner === 0 }
    }
    const front = computeRunning(1, Math.min(9, numHoles))
    const back = numHoles > 9 ? computeRunning(10, numHoles) : {}
    const overallResult = computeSegmentMatchStatus(hr, 1, numHoles)
    return {
      front, back,
      overall: computeRunning(1, numHoles),
      frontClinch: toClinch(computeSegmentMatchStatus(hr, 1, Math.min(9, numHoles))),
      backClinch: numHoles > 9 ? toClinch(computeSegmentMatchStatus(hr, 10, numHoles)) : null,
      overallClinch: toClinch(overallResult),
      overallResult,
    }
  }, [results, numHoles])

  // Stroke play running team scores (Stableford, Six Pack, High-Low)
  const strokePlayTeamScores = useMemo(() => {
    if (!results?.holeResults) return null
    const format = results.format
    if (format !== 'stableford' && format !== 'six_pack' && !isHighLow) return null

    const numTeams = teamStructure.length
    if (numTeams < 2) return null
    const running = Array.from({ length: numTeams }, () => ({}))
    const frontRunning = Array.from({ length: numTeams }, () => ({}))
    const backRunning = Array.from({ length: numTeams }, () => ({}))

    const getHoleTeamScore = (hr, teamIdx) => {
      if (isHighLow) return hr?.teamPoints?.[teamIdx] ?? null
      const ts = hr?.teamScores?.find(t => t.teamIndex === teamIdx)
      if (format === 'stableford') return ts?.teamPoints ?? null
      if (format === 'six_pack') {
        if (!ts?.playerNets?.some(p => p.net != null)) return null
        return (ts.playerNets || [])
          .filter(p => p.taken !== false && p.net != null)
          .reduce((sum, p) => sum + p.net, 0)
      }
      return null
    }

    for (let teamIdx = 0; teamIdx < numTeams; teamIdx++) {
      let cum = 0, frontCum = 0, backCum = 0
      for (let hole = 1; hole <= numHoles; hole++) {
        const hr = results.holeResults.find(h => h.hole === hole)
        const holeScore = getHoleTeamScore(hr, teamIdx)
        if (holeScore != null) {
          cum += holeScore
          running[teamIdx][hole] = cum
          if (hole <= 9) {
            frontCum += holeScore
            frontRunning[teamIdx][hole] = frontCum
          } else {
            backCum += holeScore
            backRunning[teamIdx][hole] = backCum
          }
        }
      }
    }

    // Compute diffs for 2-team matchups (Team A perspective)
    // Higher is better for Stableford and High-Low (more points = winning)
    const higherIsBetter = format === 'stableford' || isHighLow
    const sign = higherIsBetter ? 1 : -1
    let diffs = null
    if (numTeams === 2) {
      const frontBackDiff = {}
      const overallDiff = {}
      for (let hole = 1; hole <= numHoles; hole++) {
        const segR = hole <= 9 ? frontRunning : backRunning
        const a = segR[0][hole], b = segR[1][hole]
        if (a != null && b != null) frontBackDiff[hole] = (a - b) * sign
        const oa = running[0][hole], ob = running[1][hole]
        if (oa != null && ob != null) overallDiff[hole] = (oa - ob) * sign
      }
      diffs = { frontBackDiff, overallDiff, sign }
    }

    const stb = results.stableford
    const sp = results.six_pack
    const nassau = isHighLow ? results.nassau : null
    if (!stb && !sp && !nassau) return null

    // Use format identifier for display logic downstream
    const displayFormat = isHighLow ? 'high_low' : format

    return {
      format: displayFormat,
      running,
      front: stb ? stb.front.totals : sp ? sp.front.data.map(d => d.total) : [nassau.front.wins[0], nassau.front.wins[1]],
      back: stb ? stb.back.totals : sp ? sp.back.data.map(d => d.total) : [nassau.back.wins[0], nassau.back.wins[1]],
      overall: stb ? stb.overall.totals : sp ? sp.overall.data.map(d => d.total) : [nassau.overall.wins[0], nassau.overall.wins[1]],
      frontWinner: stb ? stb.front.winner : sp ? sp.front.winner : nassau.front.winner,
      backWinner: stb ? stb.back.winner : sp ? sp.back.winner : nassau.back.winner,
      overallWinner: stb ? stb.overall.winner : sp ? sp.overall.winner : nassau.overall.winner,
      diffs,
    }
  }, [results, numHoles, teamStructure.length, isHighLow])

  return {
    results,
    teamStructure,
    handicapBreakdown,
    dormieSegments,
    matchPlayHoleStatus,
    strokePlayTeamScores,
    course,
    tee,
    numHoles,
  }
}

/**
 * Backward-compat alias — returns same shape as old useRoundResults.
 */
export function useRoundResults(gameData, matchPlayers) {
  const { results, teamStructure, handicapBreakdown, dormieSegments } = useGameScoring(gameData, matchPlayers)
  return { results, teamStructure, handicapBreakdown, dormieSegments }
}

/**
 * Compute press hole statuses. Pure function (no hook).
 * Used by ActiveGame & GameDetail scorecards.
 */
export function computePressHoleStatuses(roundResults, presses, teamStructure) {
  if (!roundResults?.holeResults || !presses?.length) return []
  const isStrokePlayFormat = roundResults.type === 'stroke_play'

  // Pre-compute per-segment numbering for press labels (FP1, FP2, BP1, etc.)
  const segmentCounts = {}

  return presses.map(press => {
    const segEnd = press.metadata?.segment === 'front' ? 9 : 18
    const startHole = press.hole_start
    const isRepress = !!press.metadata?.parent_press_id
    const status = {}

    if (isStrokePlayFormat) {
      // Stroke play: show cumulative score/point differential
      let team0Total = 0, team1Total = 0
      for (let h = startHole; h <= segEnd; h++) {
        const r = roundResults.holeResults.find(x => x.hole === h)
        if (r && !r.pending && r.teamScores) {
          const t0 = r.teamScores.find(t => t.teamIndex === 0)
          const t1 = r.teamScores.find(t => t.teamIndex === 1)
          if (roundResults.format === 'stableford') {
            // Higher is better
            team0Total += t0?.teamPoints || 0
            team1Total += t1?.teamPoints || 0
            status[h] = team0Total - team1Total
          } else {
            // Lower is better (duels, six_pack) — invert so positive = team A leads
            const s0 = t0?.bestBall ?? t0?.playerNets?.filter(p => p.taken !== false && p.net != null).reduce((s, p) => s + p.net, 0) ?? 0
            const s1 = t1?.bestBall ?? t1?.playerNets?.filter(p => p.taken !== false && p.net != null).reduce((s, p) => s + p.net, 0) ?? 0
            team0Total += s0
            team1Total += s1
            status[h] = team1Total - team0Total // flip: positive = team A leading
          }
        }
      }
    } else {
      // Match play: count hole winners (or team points for High-Low)
      let diff = 0
      for (let h = startHole; h <= segEnd; h++) {
        const r = roundResults.holeResults.find(x => x.hole === h)
        if (r && !r.pending) {
          if (r.teamPoints) {
            diff += (r.teamPoints[0] - r.teamPoints[1])
          } else {
            if (r.winner === 0) diff++
            else if (r.winner === 1) diff--
          }
          status[h] = diff
        }
      }
    }

    // Compute clinch / result based on format
    let clinch = null, segResult = null
    if (!isStrokePlayFormat) {
      segResult = computeSegmentMatchStatus(roundResults.holeResults, startHole, segEnd)
      clinch = segResult?.clinchHole != null
        ? { clinchHole: segResult.clinchHole, text: segResult.text.replace(/ /g, ''), teamAWon: segResult.winner === 0 }
        : null
    } else {
      segResult = computePressResults(roundResults, { segment: press.metadata?.segment, startHole }, teamStructure)
    }

    const seg = press.metadata?.segment || 'overall'
    segmentCounts[seg] = (segmentCounts[seg] || 0) + 1
    const segLabel = getPressLabel(seg, segmentCounts[seg])

    return { press, status, segment: seg, startHole, clinch, segResult, isRepress, segLabel }
  })
}
